import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router';
import { catchError, EMPTY, Observable, throwError } from 'rxjs'
import { NotificationService } from '../services/notification.service';
import { Message } from '../services/device.service';
import * as Sentry from "@sentry/angular";
import { EnvironmentService } from '../services/environment.service';
import { FocusTrackerService } from '../services/focus-tracker.service';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {

  notificationService = inject(NotificationService)
  private environmentService = inject(EnvironmentService);
  private tracker = inject(FocusTrackerService);
  private router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipIntercept = request.headers.has('skip');
    if (skipIntercept) {
      request = request.clone({
        headers: request.headers.delete('skip')
      });
    }
    return next.handle(request).pipe(catchError((err: any) => {
      const errorResponse = err as HttpErrorResponse;

      // Submit/cancel blocked by the Gateway geo/IP check: the session is fine, the location is not.
      // Send the customer to Location Required (it re-checks and resumes the stage) instead of
      // "session expired", and complete so the stage page doesn't add its own error toast.
      if (!skipIntercept && isGeoBlocked(errorResponse)) {
        Sentry.captureMessage(`Secure Data blocked by location: ${request.method} ${request.url}`, {
          level: 'warning',
          tags: {
            geo_blocked: 'true',
            status: errorResponse.status.toString(),
            method: request.method,
            app_version: this.environmentService.env.appVersion,
          },
        });
        this.router.navigate(['location-required'], { queryParamsHandling: 'preserve' });
        return EMPTY;
      }

      if (err && err.error && typeof err.error === 'string' && JSON.parse(err.error).ErrorDescription) {
        err.error = JSON.parse(err.error) as AppErrorResponse;
      } else {
        err.error = {
          Error: Message.Error,
          ErrorDescription: err.error?.ErrorDescription
        } as AppErrorResponse
      }

      const rethrowErr = errorResponse instanceof Error
        ? errorResponse
        : new Error(`HTTP ${errorResponse.status} ${request.method} ${request.url}`);

      if (errorResponse.status >= 400 && errorResponse.status != 410) {
        Sentry.captureException(rethrowErr, {
          tags: {
            http_error: 'true',
            status: errorResponse.status.toString(),
            method: request.method,
            app_version: this.environmentService.env.appVersion,
            last_control: this.tracker?.lastControl,
            last_control_tag: this.tracker?.lastTag,
          },
          extra: {
            url: request.url,
            statusText: errorResponse.statusText,
            body: request.body,
            response: errorResponse.error,
            rawResponse: {
              // include the response shape but avoid circular refs
              status: errorResponse.status,
              headers: errorResponse.headers?.keys ? errorResponse.headers.keys() : undefined,
              message: (errorResponse as any).message,
              name: (errorResponse as any).name,
            },
          },
        })
      }

      if (!skipIntercept) {
        if (errorResponse.status === 401 || errorResponse.status === 403) {
          this.notificationService.showError(errorResponse.status === 401 ? Message.NotAuthporized : Message.Forbidden, Message.Error);
        }
        else if (errorResponse.status == 400) {
          this.notificationService.showError(err.error.ErrorDescription, Message.Error);
        }
        else if (errorResponse.status == 410) {
          this.notificationService.showError(Message.ErrorMessageExpiredLink, Message.Error);
        } else {
          this.notificationService.showError(Message.ErrorMessage, Message.Error);
        }
      }
      return throwError(() => rethrowErr);
    }));
  }
}

/**
 * Gateway answers a geo/IP denial with 403 and either { ErrorCode: 'GEO_BLOCKED' } (cancel) or
 * { Error: 'GeoAccessDeniedException' } (submit, through ErrorExceptionHandler). Bodies arrive as text.
 */
export function isGeoBlocked(errorResponse: HttpErrorResponse): boolean {
  if (errorResponse?.status !== 403) return false;
  let body = errorResponse.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return false;
    }
  }
  const errorCode = String(body?.ErrorCode ?? '').trim().toLowerCase();
  const errorName = String(body?.Error ?? '').trim().toLowerCase();
  return errorCode === 'geo_blocked' || errorName === 'geoaccessdeniedexception';
}

export interface AppErrorResponse {
  ErrorDescription?: string;
  Error?: string;
  StackTrace?: string;
}